/**
 * 媒體庫縮圖與預覽（Phase 0）。
 *
 * 內容端點需要 Authorization header，`<img src>` / `<video src>` 帶不了 header，
 * 因此一律先 fetch 成 blob 再轉 object URL；元件卸載時務必 revoke，否則在媒體
 * 庫這種會反覆切換篩選的頁面上，影片 blob 會持續累積佔用記憶體。
 */

import { useEffect, useState } from 'react';

import { fetchMediaObjectUrl, type MediaItem } from '../lib/api-client';

const TYPE_ICON: Record<MediaItem['media_type'], string> = {
  image: '🖼️',
  video: '🎬',
  audio: '🔊',
  document: '📄',
};

/**
 * 取 object URL 並在 id 變更 / 卸載時 revoke。
 *
 * 「還在載」與「載不到」必須分得出來：資料列存在但物件不在儲存體裡是這個媒體庫
 * 真實會發生的狀況（匯入中斷、bucket 被清），而永遠轉圈的載入中會被當成畫面壞掉，
 * 沒有人會想到去查物件。
 */
function useObjectUrl(
  id: string | null,
  enabled: boolean,
): { url: string | null; failed: boolean } {
  const [state, setState] = useState<{ url: string | null; failed: boolean }>({
    url: null,
    failed: false,
  });

  useEffect(() => {
    if (!id || !enabled) {
      setState({ url: null, failed: false });
      return;
    }
    let revoked = false;
    let created: string | null = null;
    setState({ url: null, failed: false });
    void fetchMediaObjectUrl(id)
      .then((u) => {
        if (revoked) {
          URL.revokeObjectURL(u);
          return;
        }
        created = u;
        setState({ url: u, failed: false });
      })
      .catch(() => {
        if (!revoked) setState({ url: null, failed: true });
      });

    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
      setState({ url: null, failed: false });
    };
  }, [id, enabled]);

  return state;
}

/**
 * 縮圖。`size` 是寬度上限（高度取 3:4），清單用預設的 56，網格傳 160。
 *
 * 非圖片退回型別圖示並附上副檔名——網格檢視裡一整片 🎬 分不出誰是誰，而副檔名
 * 至少能區分 mp4 與 wav。
 */
export function MediaThumb({ item, size = 56 }: { item: MediaItem; size?: number }): JSX.Element {
  // 只有圖片載縮圖；影片／音訊在列表載入等同於下載整個檔案，代價不成比例
  const { url } = useObjectUrl(item.id, item.media_type === 'image');

  const box: React.CSSProperties = {
    width: size,
    height: Math.round(size * 0.75),
    borderRadius: 4,
    border: '1px solid #e5e7eb',
    background: '#f9fafb',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    overflow: 'hidden',
    fontSize: Math.max(18, Math.round(size / 3.5)),
  };

  if (url) {
    return (
      <div style={box}>
        <img src={url} alt={item.filename} style={{ maxWidth: '100%', maxHeight: '100%' }} />
      </div>
    );
  }
  const ext = item.filename.includes('.') ? item.filename.split('.').pop() : null;
  return (
    <div style={box}>
      <span>{TYPE_ICON[item.media_type]}</span>
      {size >= 100 && ext && (
        <span style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>{ext}</span>
      )}
    </div>
  );
}

/**
 * 只有 media id 時的縮圖。
 *
 * 推薦點只存 `photo_media_id` 這類外鍵，沒有 filename／media_type 可帶——而
 * 內容端點本來就只需要 id。逐一查媒體詳情只為了拿檔名，等於 20 筆卡片乘三張圖
 * 共 60 次請求，代價不成比例。
 *
 * 一律當圖片載：三個推薦點插槽在後端就強制 PNG，不會出現影片。
 */
export function MediaThumbById({
  id,
  size = 56,
  alt,
}: {
  id: string | null;
  size?: number;
  alt: string;
}): JSX.Element {
  const { url } = useObjectUrl(id, id !== null);
  const box: React.CSSProperties = {
    width: size,
    height: Math.round(size * 0.75),
    borderRadius: 4,
    border: `1px ${id === null ? 'dashed' : 'solid'} #e5e7eb`,
    background: '#f9fafb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    fontSize: 11,
    color: '#9ca3af',
    flexShrink: 0,
  };

  if (url) {
    return (
      <div style={box}>
        <img src={url} alt={alt} style={{ maxWidth: '100%', maxHeight: '100%' }} />
      </div>
    );
  }
  return <div style={box}>{id === null ? '未設定' : '⋯'}</div>;
}

/**
 * 內容本體（不含外框與中繼資料）。
 *
 * 媒體庫的預覽視窗與挑選器的側欄共用同一份。分開寫的話，「wav 要能播、mp4 要有
 * 控制列、document 只能下載」這組判斷會在兩邊各長一套然後漂移。
 *
 * 與縮圖不同，這裡**一律載內容**——會叫到它就代表使用者指名要看這一個，此時
 * 下載整支影片是應該付的代價；縮圖那邊不載是因為那是一整頁 50 個一起。
 */
export function MediaInlinePreview({
  item,
  maxHeight = '65vh',
  autoPlayAudio = false,
}: {
  item: MediaItem;
  maxHeight?: string;
  /** 語音插槽逐支試聽時省一次點擊；點列表本身就是使用者手勢，不會被瀏覽器擋。 */
  autoPlayAudio?: boolean;
}): JSX.Element {
  const { url, failed } = useObjectUrl(item.id, true);

  if (failed) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#b45309', fontSize: 13 }}>
        ⚠ 取不到檔案內容
        <div style={{ color: '#6b7280', marginTop: 4 }}>
          資料庫有這一列，但物件儲存體裡找不到對應檔案（匯入中斷或 bucket 被清）。
          <br />
          這一筆掛上去，發布時會被擋下——請回媒體庫重新上傳。
        </div>
      </div>
    );
  }
  if (!url) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>載入中…</div>;
  }
  if (item.media_type === 'image') {
    return <img src={url} alt={item.filename} style={{ maxWidth: '100%', maxHeight }} />;
  }
  if (item.media_type === 'video') {
    return <video key={item.id} src={url} controls style={{ maxWidth: '100%', maxHeight }} />;
  }
  if (item.media_type === 'audio') {
    return (
      <audio
        key={item.id}
        src={url}
        controls
        autoPlay={autoPlayAudio}
        style={{ width: '100%', maxWidth: 420 }}
      />
    );
  }
  return (
    <a href={url} download={item.filename}>
      下載 {item.filename}
    </a>
  );
}

export function MediaPreviewModal({
  item,
  onClose,
}: {
  item: MediaItem;
  onClose: () => void;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: 16,
          maxWidth: '80vw',
          maxHeight: '85vh',
          overflow: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            marginBottom: 12,
          }}
        >
          <strong style={{ fontSize: 14 }}>{item.filename}</strong>
          <button onClick={onClose} style={{ cursor: 'pointer' }}>
            關閉 (Esc)
          </button>
        </div>

        <MediaInlinePreview item={item} />

        <dl
          style={{
            marginTop: 14,
            fontSize: 12,
            color: '#374151',
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '4px 12px',
          }}
        >
          <dt>MIME</dt>
          <dd style={{ fontFamily: 'monospace' }}>{item.mime_type}</dd>
          <dt>大小</dt>
          <dd>{(Number(item.size_bytes) / 1024 / 1024).toFixed(2)} MB</dd>
          <dt>語系</dt>
          <dd>{item.language ?? '—'}</dd>
          <dt>標籤</dt>
          <dd>{item.tags.length > 0 ? item.tags.join('、') : '—'}</dd>
          <dt>sha256</dt>
          <dd style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{item.sha256}</dd>
          <dt>物件鍵</dt>
          <dd style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{item.storage_url}</dd>
        </dl>
      </div>
    </div>
  );
}
